import React, { useId, useMemo, useState } from 'react';
import {
  LuActivity,
  LuArrowUpRight,
  LuCircleCheck,
  LuCircleX,
  LuHash,
  LuInbox,
  LuInfo,
  LuLayers,
  LuSparkles,
  LuTriangleAlert,
} from 'react-icons/lu';
import '../../styles/rt2-primitives.css';
import './ui.css';
import Pagination from './Pagination';
import { ActionMenu, RowActions } from './ActionMenu';
import Select from './Select';
import { RtModal, RtModalSection, RtModalField, RtModalActions } from './RtModal';

const METRIC_TONE_ICONS = {
  primary: LuActivity,
  success: LuCircleCheck,
  danger: LuCircleX,
  warning: LuTriangleAlert,
  info: LuInfo,
  accent: LuSparkles,
  violet: LuLayers,
};

const guessMetricTone = (label = '') => {
  const l = String(label).toLowerCase();
  if (/fail|error|danger|critical/.test(l)) return 'danger';
  if (/pass|success|heal|active|ready/.test(l)) return 'success';
  if (/warn|flaky|draft|pending/.test(l)) return 'warning';
  if (/skip|duration|time|env|owner|trigger/.test(l)) return 'info';
  if (/cover|automat|suite|plan|case/.test(l)) return 'accent';
  if (/dup|group|epic|release|sprint/.test(l)) return 'violet';
  return 'primary';
};

const W = 100;
const H = 36;
const PAD_Y = 4;

const smoothPath = (pts) => {
  if (pts.length < 2) return '';
  let d = `M${pts[0][0]},${pts[0][1]}`;
  for (let i = 0; i < pts.length - 1; i += 1) {
    const p0 = pts[i - 1] || pts[i];
    const p1 = pts[i];
    const p2 = pts[i + 1];
    const p3 = pts[i + 2] || p2;
    const c1x = p1[0] + (p2[0] - p0[0]) / 6;
    const c1y = p1[1] + (p2[1] - p0[1]) / 6;
    const c2x = p2[0] - (p3[0] - p1[0]) / 6;
    const c2y = p2[1] - (p3[1] - p1[1]) / 6;
    d += ` C${c1x},${c1y} ${c2x},${c2y} ${p2[0]},${p2[1]}`;
  }
  return d;
};

/**
 * Compact trend line for metric cards. Renders nothing for fewer than two points
 * so a single snapshot is never presented as a trend.
 */
const Sparkline = ({ data = [], labels = [], format = (v) => v, className = '' }) => {
  const gradientId = useId().replace(/:/g, '');
  const [hover, setHover] = useState(null);

  const values = useMemo(() => data.map((v) => Number(v) || 0), [data]);

  const points = useMemo(() => {
    if (values.length < 2) return [];
    const min = Math.min(...values);
    const max = Math.max(...values);
    const span = max - min || 1;
    return values.map((v, i) => [
      (i / (values.length - 1)) * W,
      max === min ? H / 2 : H - PAD_Y - ((v - min) / span) * (H - PAD_Y * 2),
    ]);
  }, [values]);

  if (points.length < 2) return null;

  const line = smoothPath(points);
  const area = `${line} L${W},${H} L0,${H} Z`;
  const active = hover ?? points.length - 1;
  const [ax, ay] = points[active];

  const onMove = (e) => {
    const box = e.currentTarget.getBoundingClientRect();
    const ratio = Math.min(Math.max((e.clientX - box.left) / box.width, 0), 1);
    setHover(Math.round(ratio * (points.length - 1)));
  };

  const summary = values
    .map((v, i) => `${labels[i] ? `${labels[i]}: ` : ''}${format(v)}`)
    .join(', ');

  return (
    <div
      className={`rt-sparkline ${hover != null ? 'rt-sparkline--hover' : ''} ${className}`.trim()}
      onMouseMove={onMove}
      onMouseLeave={() => setHover(null)}
      role="img"
      aria-label={`Trend: ${summary}`}
    >
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" aria-hidden="true">
        <defs>
          <linearGradient id={gradientId} x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor="currentColor" stopOpacity="0.28" />
            <stop offset="100%" stopColor="currentColor" stopOpacity="0" />
          </linearGradient>
        </defs>
        <path className="rt-sparkline__area" d={area} fill={`url(#${gradientId})`} />
        <path className="rt-sparkline__line" d={line} />
      </svg>
      <span
        className="rt-sparkline__dot"
        style={{ left: `${(ax / W) * 100}%`, top: `${(ay / H) * 100}%` }}
        aria-hidden="true"
      />
      {hover != null && (
        <span
          className="rt-sparkline__tip"
          style={{ left: `${(ax / W) * 100}%`, transform: `translateX(-${(ax / W) * 100}%)` }}
          aria-hidden="true"
        >
          {labels[hover] && <em>{labels[hover]}</em>}
          {format(values[hover])}
        </span>
      )}
    </div>
  );
};



export { Select, Sparkline };

export { Pagination, ActionMenu, RowActions };
export function Button({
  children,
  variant = 'primary',
  size = 'md',
  icon,
  iconRight,
  fullWidth,
  loading,
  disabled,
  className = '',
  type = 'button',
  ...props
}) {
  const mappedVariant = variant === 'ghost' || variant === 'tertiary' ? 'ghost' : variant;
  return (
    <button
      type={type}
      className={`rt-btn rt-btn--${mappedVariant} rt-btn--${size} ${fullWidth ? 'rt-btn--full' : ''} ${className}`.trim()}
      disabled={disabled || loading}
      {...props}
    >
      {loading ? <span className="rt-btn__spinner" aria-hidden /> : icon}
      {children != null && children !== false && <span>{children}</span>}
      {!loading && iconRight}
    </button>
  );
}

export function IconButton({
  children,
  label,
  variant = 'ghost',
  size = 'md',
  className = '',
  ...props
}) {
  return (
    <button
      type="button"
      className={`rt-icon-btn rt-icon-btn--${variant} rt-icon-btn--${size} ${className}`.trim()}
      aria-label={label}
      title={label}
      {...props}
    >
      {children}
    </button>
  );
}

export function Avatar({ name = '', src, size = 'md', className = '' }) {
  const initials = String(name)
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase())
    .join('') || '?';

  return (
    <span className={`rt-avatar rt-avatar--${size} ${className}`.trim()} title={name}>
      {src ? <img src={src} alt="" /> : initials}
    </span>
  );
}

export const PageHeader = ({
  eyebrow,
  title,
  description,
  subtitle,
  actions,
  breadcrumbs,
}) => (
  <div className="rt-page-header">
    <div className="rt-page-header__left">
      {breadcrumbs}
      {eyebrow && <p className="rt-page-header__eyebrow">{eyebrow}</p>}
      <h1 className="rt-page-header__title">{title}</h1>
      {(description || subtitle) && (
        <p className="rt-page-header__desc">{description || subtitle}</p>
      )}
    </div>
    {actions && <div className="rt-page-header__actions">{actions}</div>}
  </div>
);

export const SectionHeader = ({ title, actions }) => (
  <div className="rt-section-header">
    <h2 className="rt-section-header__title">{title}</h2>
    {actions}
  </div>
);

export const Breadcrumbs = ({ items = [], onNavigate }) => (
  <nav className="rt-breadcrumbs" aria-label="Breadcrumb">
    {items.map((item, idx) => {
      const isLast = idx === items.length - 1;
      return (
        <React.Fragment key={`${item.label}-${idx}`}>
          {idx > 0 && <span className="rt-breadcrumbs__sep">/</span>}
          {isLast || !item.path ? (
            <span className="rt-breadcrumbs__current">{item.label}</span>
          ) : (
            <button
              type="button"
              className="rt-breadcrumbs__link"
              onClick={() => onNavigate?.(item.path)}
            >
              {item.label}
            </button>
          )}
        </React.Fragment>
      );
    })}
  </nav>
);

export const SearchInput = ({ value, onChange, placeholder = 'Search…', className = '' }) => (
  <label className={`rt-search ${className}`.trim()}>
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="11" cy="11" r="7" />
      <path d="m20 20-3.5-3.5" />
    </svg>
    <input
      value={value}
      onChange={(e) => onChange?.(e.target.value)}
      placeholder={placeholder}
      aria-label={placeholder}
    />
  </label>
);

export const FilterBar = ({ children, className = '' }) => (
  <div className={`rt-filter-bar ${className}`.trim()}>{children}</div>
);

export const Tabs = ({ tabs = [], activeId, onChange }) => (
  <div className="rt-tabs" role="tablist">
    {tabs.map((tab) => (
      <button
        key={tab.id}
        type="button"
        role="tab"
        aria-selected={tab.id === activeId}
        className={`rt-tabs__tab${tab.id === activeId ? ' rt-tabs__tab--active' : ''}`}
        onClick={() => onChange?.(tab.id)}
      >
        {tab.label}
        {tab.count != null && <span style={{ marginLeft: 6, opacity: 0.7 }}>({tab.count})</span>}
      </button>
    ))}
  </div>
);

export const StatusBadge = ({ status, children }) => {
  const raw = String(status || children || '').toLowerCase();
  let variant = 'neutral';
  if (['passed', 'pass', 'active', 'success', 'completed', 'healthy', 'done'].some((k) => raw.includes(k))) {
    variant = 'success';
  } else if (['failed', 'fail', 'error', 'critical', 'blocked'].some((k) => raw.includes(k))) {
    variant = 'danger';
  } else if (['pending', 'warning', 'flaky', 'review', 'to do', 'todo'].some((k) => raw.includes(k))) {
    variant = 'warning';
  } else if (['running', 'in_progress', 'in progress', 'info'].some((k) => raw.includes(k))) {
    variant = 'info';
  }
  return (
    <span className={`rt-badge rt-badge--${variant}`}>
      <span className={`rt-status-dot rt-status-dot--${variant}`} />
      {children || status || '—'}
    </span>
  );
};

export const PriorityBadge = ({ priority }) => {
  const raw = String(priority || '').toLowerCase();
  let variant = 'neutral';
  if (raw === 'critical' || raw === 'blocker') variant = 'danger';
  else if (raw === 'high') variant = 'warning';
  else if (raw === 'medium' || raw === 'normal') variant = 'info';
  else if (raw === 'low') variant = 'neutral';
  return <span className={`rt-badge rt-badge--${variant}`}>{priority || '—'}</span>;
};

export const MetricCard = ({
  label,
  value,
  icon,
  trend,
  context,
  className = '',
  tone,
  sparkline,
  trendInvert = false,
  onClick,
}) => {
  const clickable = typeof onClick === 'function';
  const falling = String(trend).startsWith('-') || String(trend).startsWith('↓');
  const good = trendInvert ? falling : !falling;
  const resolvedTone = tone || guessMetricTone(label);
  const ToneIcon = METRIC_TONE_ICONS[resolvedTone] || LuHash;
  const resolvedIcon = icon ?? <ToneIcon size={14} />;
  const classes = [
    'rt-metric-card',
    resolvedTone ? `rt-metric-card--${resolvedTone}` : '',
    clickable ? 'rt-metric-card--clickable' : '',
    sparkline?.data?.length > 1 ? 'rt-metric-card--spark' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div
      className={classes}
      onClick={onClick}
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      onKeyDown={
        clickable
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick(e);
              }
            }
          : undefined
      }
    >
      <div className="rt-metric-card__top">
        <div className="rt-metric-card__label">{label}</div>
        <span className="rt-metric-card__badge" aria-hidden>
          <span className="rt-metric-card__icon">{resolvedIcon}</span>
          {clickable && (
            <span className="rt-metric-card__go">
              <LuArrowUpRight size={14} />
            </span>
          )}
        </span>
      </div>
      <div className="rt-metric-card__body">
        <div className="rt-metric-card__main">
          <div className="rt-metric-card__value">{value}</div>
          {(trend || context) && (
            <div className="rt-metric-card__meta">
              {trend != null && trend !== '' && (
                <span
                  className={`rt-metric-card__trend ${
                    good ? 'rt-metric-card__trend--up' : 'rt-metric-card__trend--down'
                  }`}
                >
                  {trend}
                </span>
              )}
              {context && <span className="rt-metric-card__context">{context}</span>}
            </div>
          )}
        </div>
        {sparkline?.data?.length > 1 && (
          <Sparkline
            className="rt-metric-card__spark"
            data={sparkline.data}
            labels={sparkline.labels}
            format={sparkline.format}
          />
        )}
      </div>
    </div>
  );
};

export const EmptyState = ({ title = 'Nothing here yet', description, action, icon }) => (
  <div className="rt-state" role="status">
    <span className="rt-state__icon" aria-hidden="true">{icon || <LuInbox size={20} />}</span>
    <h3 className="rt-state__title">{title}</h3>
    {description && <p className="rt-state__desc">{description}</p>}
    {action}
  </div>
);

export const LoadingState = ({ label = 'Loading…' }) => (
  <div className="rt-state" role="status" aria-live="polite">
    <div className="rt-skeleton" style={{ width: 40, height: 40, borderRadius: '50%' }} />
    <p className="rt-state__desc">{label}</p>
  </div>
);

export const ErrorState = ({ title = 'Something went wrong', description, onRetry, retryLabel = 'Try Again' }) => (
  <div className="rt-state rt-state--error" role="alert">
    <span className="rt-state__icon" aria-hidden="true"><LuTriangleAlert size={20} /></span>
    <h3 className="rt-state__title">{title}</h3>
    {description && <p className="rt-state__desc">{description}</p>}
    {onRetry && (
      <button type="button" className="rt-btn rt-btn--secondary" onClick={onRetry}>
        {retryLabel}
      </button>
    )}
  </div>
);

export const DataTable = ({
  columns = [],
  rows = [],
  rowKey = 'id',
  onRowClick,
  emptyTitle = 'No results',
  emptyDescription,
  loading = false,
  skeletonRows = 8,
}) => {
  if (loading) {
    return (
      <div className="rt-data-table-wrap">
        <table className="rt-data-table rt-table-skeleton">
          <thead>
            <tr>
              {columns.map((col) => (
                <th key={col.key} style={col.width ? { width: col.width } : undefined}>
                  {col.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: skeletonRows }).map((_, idx) => (
              <tr key={`sk-${idx}`}>
                {columns.map((col) => (
                  <td key={col.key}>
                    <div className="rt-skeleton" style={{ width: `${60 + ((idx + col.key.length) % 30)}%` }} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  if (!rows.length) {
    return <EmptyState title={emptyTitle} description={emptyDescription} />;
  }

  return (
    <div className="rt-data-table-wrap">
      <table className="rt-data-table">
        <thead>
          <tr>
            {columns.map((col) => (
              <th key={col.key} style={col.width ? { width: col.width } : undefined}>
                {col.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, idx) => {
            const key = row[rowKey] ?? idx;
            return (
              <tr
                key={key}
                className={onRowClick ? 'rt-data-table__row--clickable' : undefined}
                onClick={() => onRowClick?.(row)}
              >
                {columns.map((col) => (
                  <td key={col.key}>
                    {col.render ? col.render(row) : row[col.key]}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export const BulkActionBar = ({ count, children, onClear }) => {
  if (!count) return null;
  return (
    <div className="rt-bulk-bar">
      <span>{count} selected</span>
      <div className="rt-bulk-bar__actions">
        {children}
        {onClear && (
          <button type="button" className="rt-btn rt-btn--secondary rt-btn--sm" onClick={onClear}>
            Clear
          </button>
        )}
      </div>
    </div>
  );
};

export const FormDrawer = ({
  open,
  onClose,
  title,
  children,
  footer,
  wide = false,
}) => {
  if (!open) return null;
  return (
    <div className="rt-drawer-overlay" onClick={onClose} role="presentation">
      <aside
        className={`rt-drawer${wide ? ' rt-drawer--wide' : ''}`}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        <div className="rt-drawer__header">
          <h2 className="rt-drawer__title">{title}</h2>
          <button type="button" className="rt-btn rt-btn--tertiary rt-btn--icon" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>
        <div className="rt-drawer__body">{children}</div>
        {footer && <div className="rt-drawer__footer">{footer}</div>}
      </aside>
    </div>
  );
};

export { RtModal, RtModalSection, RtModalField, RtModalActions };

export const ConfirmDialog = ({
  open,
  title,
  body,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  danger = false,
  onConfirm,
  onCancel,
}) => {
  if (!open) return null;
  return (
    <RtModal
      open={open}
      onClose={onCancel}
      title={title}
      size="sm"
      variant={danger ? 'destructive' : 'confirm'}
      role="alertdialog"
      closeOnEscape
      closeOnBackdrop
      footer={
        <>
          {cancelLabel != null && cancelLabel !== false ? (
            <button type="button" className="rt-btn rt-btn--secondary" onClick={onCancel}>
              {cancelLabel}
            </button>
          ) : null}
          <button
            type="button"
            className={`rt-btn ${danger ? 'rt-btn--danger' : 'rt-btn--primary'}`}
            onClick={onConfirm}
            autoFocus
          >
            {confirmLabel}
          </button>
        </>
      }
    >
      {body != null && body !== '' ? <p className="rt-modal-confirm__message">{body}</p> : null}
    </RtModal>
  );
};

export const PageScaffold = ({
  eyebrow,
  title,
  description,
  actions,
  breadcrumbs,
  children,
  embedLegacy = false,
  /** When true, only breadcrumbs + actions render (no H1) — use when body already has the entity title. */
  hideHeader = false,
  /** Compact chrome: breadcrumbs + actions only, no description block. */
  compact = false,
}) => (
  <div className={`rt-page${embedLegacy ? ' rt-legacy-embed' : ''}${compact ? ' rt-page--compact' : ''}`}>
    {!hideHeader && (title || breadcrumbs || actions) && (
      <PageHeader
        eyebrow={compact ? undefined : eyebrow}
        title={title}
        description={compact ? undefined : description}
        actions={actions}
        breadcrumbs={breadcrumbs}
      />
    )}
    {hideHeader && (breadcrumbs || actions) && (
      <div className="rt-page-header rt-page-header--slim">
        <div className="rt-page-header__left">{breadcrumbs}</div>
        {actions && <div className="rt-page-header__actions">{actions}</div>}
      </div>
    )}
    {children}
  </div>
);

export default {
  Button,
  IconButton,
  Avatar,
  PageHeader,
  SectionHeader,
  Breadcrumbs,
  SearchInput,
  FilterBar,
  Tabs,
  StatusBadge,
  PriorityBadge,
  MetricCard,
  EmptyState,
  LoadingState,
  ErrorState,
  DataTable,
  BulkActionBar,
  FormDrawer,
  ConfirmDialog,
  RtModal,
  RtModalSection,
  RtModalField,
  RtModalActions,
  PageScaffold,
  Pagination,
  ActionMenu,
  RowActions,
};
